import {FileText, RefreshCw} from 'lucide-react'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {useI18n} from '@/i18n'
import {MutedText} from '@/components/shared/MutedText'
import {formatTimestamp} from '@/lib/date-utils'
import type {OutboundConnectorLog} from '@/types/outbound-connector'

interface ConnectorLogViewProps {
    logs: OutboundConnectorLog[]
    onRefresh: () => void
}

export function ConnectorLogView({logs, onRefresh}: ConnectorLogViewProps) {
    const i18n = useI18n()

    const getLogStatusBadge = (log: OutboundConnectorLog) => {
        if (log.success) {
            return <Badge variant={BadgeVariant.SUCCESS}>{log.statusCode || i18n.connectors.connectionSuccess}</Badge>
        }
        if (log.errorMessage) {
            return <Badge variant="destructive">{log.statusCode || i18n.connectors.connectionFailed}</Badge>
        }
        return <Badge variant="outline">{i18n.connectors.pending}</Badge>
    }

    return (
        <Card>
            <RowCardHeader>
                <div>
                    <CardTitle className="items-center gap-2" data-testid="card-title-recent-activity">
                        <FileText className="h-5 w-5"/>
                        {i18n.connectors.recentActivity}
                    </CardTitle>
                    <CardDescription>
                        {i18n.connectors.last20Calls}
                    </CardDescription>
                </div>
                <Button data-testid="btn-refresh-logs" variant="ghost" size="sm" onClick={onRefresh}>
                    <RefreshCw className="h-4 w-4"/>
                </Button>
            </RowCardHeader>
            <CardContent>
                {logs.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-timestamp">{i18n.connectors.timestamp}</TableHead>
                                <TableHead data-testid="col-operation">{i18n.connectors.operation}</TableHead>
                                <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                <TableHead data-testid="col-duration">{i18n.connectors.duration}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell className="font-mono text-sm">
                                        {formatTimestamp(log.createdAt)}
                                    </TableCell>
                                    <TableCell>
                                        <span className="items-center gap-1.5">
                                            <Badge variant="outline">{log.operation}</Badge>
                                            {log.batchId && (
                                                <Badge variant="secondary" className="text-xs" title={log.batchId}>
                                                    {i18n.connectors.batch}
                                                </Badge>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {getLogStatusBadge(log)}
                                        {log.errorMessage && (
                                            <span
                                                className="text-destructive text-sm block truncate max-w-xs"
                                                title={log.errorMessage}
                                            >
                                                {log.errorMessage}
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {log.durationMs ? i18n.common.valueWithMs({value: log.durationMs}) : '\u2014'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <MutedText block centered className="py-4">
                        {i18n.connectors.noActivity}
                    </MutedText>
                )}
            </CardContent>
        </Card>
    )
}
